import React from "react";
import Logo from "./Logo";
import { useContent } from "../lib/ContentContext";

// A single "por cenário" card
function ScenarioBag({ n, title, tag, looks, extras }) {
  return (
    <div
      data-testid={`bagagem-cenario-${n}`}
      className="grid grid-cols-1 md:grid-cols-12 gap-0 border-t border-neutral-300 py-10 md:py-12"
    >
      {/* Left: title + tag */}
      <div className="md:col-span-4 md:pr-8">
        <div className="serif text-[52px] leading-none text-[color:var(--alta-red)]">
          {String(n).padStart(2, "0")}
        </div>
        <h3 className="mt-4 serif text-[26px] md:text-[30px] leading-[1.05] tracking-[-0.01em] text-neutral-900">
          {title}
        </h3>
        <div className="mt-3 mono text-[10px] uppercase tracking-[0.28em] text-[#5A6B52] flex items-center gap-2">
          <span className="w-6 h-px bg-[#5A6B52]" />
          <span>{tag}</span>
        </div>
      </div>

      {/* Middle: looks */}
      <div className="md:col-span-4 md:px-8 md:border-l md:border-neutral-300 mt-6 md:mt-0">
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
          Roupas &amp; Looks
        </div>
        <div className="mt-4 text-[14px] leading-[1.75] text-neutral-800 whitespace-pre-line font-light">
          {looks}
        </div>
      </div>

      {/* Right: extras */}
      <div className="md:col-span-4 md:px-8 md:border-l md:border-neutral-300 mt-6 md:mt-0">
        <div className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
          Acessórios &amp; Extras
        </div>
        <div className="mt-4 text-[14px] leading-[1.75] text-neutral-800 whitespace-pre-line font-light">
          {extras}
        </div>
      </div>
    </div>
  );
}

// A single "preparação pessoal" card
function PrepCard({ n, title, body }) {
  return (
    <div
      data-testid={`bagagem-prep-${n}`}
      className="border border-[#F9F8F6]/15 p-8 hover:border-[color:var(--alta-red)]/70 transition-colors duration-500 flex flex-col"
    >
      <div className="serif text-[42px] leading-none text-[#F9F8F6]">
        {String(n).padStart(2, "0")}
      </div>
      <div className="mt-4 h-px bg-[color:var(--alta-red)]/60 w-8" />
      <h4 className="mt-5 serif text-[22px] md:text-[26px] leading-tight text-[#F9F8F6]">
        {title}
      </h4>
      <p className="mt-4 text-[13.5px] leading-[1.75] text-[#F9F8F6]/75 font-light">
        {body}
      </p>
    </div>
  );
}

/**
 * A Bagagem — packing list section for the guest logged area.
 * Two visually distinct blocks:
 *  1) By-scenario list (light bg) — 5 cards
 *  2) Personal prep (dark bg) — 3 cards
 * All text/content is CMS-editable (content.bagagem.*).
 */
export default function Bagagem() {
  const { content } = useContent();
  const t = content.bagagem;

  const cenarios = [1, 2, 3, 4, 5].map((n) => ({
    n,
    title: t[`c${n}_title`],
    tag: t[`c${n}_tag`],
    looks: t[`c${n}_looks`],
    extras: t[`c${n}_extras`],
  }));

  const preps = [1, 2, 3].map((n) => ({
    n,
    title: t[`p${n}_title`],
    body: t[`p${n}_body`],
  }));

  return (
    <>
      {/* Bloco 1 — Por cenário (fundo claro) */}
      <section
        data-testid="bagagem-cenarios"
        className="py-24 md:py-36 bg-[#F3F1EA] border-t border-neutral-200 scroll-mt-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {/* Header */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-10">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red)]">
                <span className="rec-dot" aria-hidden="true" />
                <span>{t.eyebrow}</span>
              </div>
              <h2 className="mt-8 serif text-[42px] md:text-[64px] leading-[0.98] tracking-[-0.01em] text-neutral-900 whitespace-pre-line">
                {t.title}
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-[15px] leading-[1.75] text-neutral-700 max-w-[52ch] font-light">
                {t.body}
              </p>
              <div className="mt-6 flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                <span className="w-8 h-px bg-neutral-400" />
                <span>{t.footnote}</span>
              </div>
            </div>
          </div>

          {/* Cards por cenário */}
          <div className="mt-6">
            {cenarios.map((c) => (
              <ScenarioBag key={c.n} {...c} />
            ))}
          </div>
        </div>
      </section>

      {/* Bloco 2 — Preparação pessoal (fundo escuro) */}
      <section
        data-testid="bagagem-preparacao"
        className="relative py-24 md:py-36 bg-[#1A1A1A] text-[#F9F8F6] overflow-hidden border-y border-[#F9F8F6]/10"
      >
        <div className="absolute -left-24 -bottom-16 opacity-[0.05] pointer-events-none select-none hidden md:block">
          <Logo variant="light" size={480} testid="bagagem-prep-watermark" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--alta-red-soft)]">
              <span className="rec-dot" aria-hidden="true" />
              <span>{t.p_eyebrow}</span>
            </div>
            <h2 className="mt-8 serif text-[38px] md:text-[52px] leading-[1] tracking-[-0.01em] whitespace-pre-line">
              {t.p_title}
            </h2>
            <p className="mt-8 max-w-[42ch] text-[13px] leading-[1.8] text-[#F9F8F6]/70 font-light">
              {t.p_body}
            </p>
          </div>
          <div className="md:col-span-7 md:col-start-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {preps.map((p) => (
              <PrepCard key={p.n} {...p} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
